import { AnalysisRange, TrendChartPoint } from '../types/health.types';

export type HealthStatus = 'normal' | 'warning' | 'unknown';

export interface WindowHealth {
  /** Worst status across the whole plotted window. */
  status: HealthStatus;
  /** How many plotted points fall outside the acceptable range. */
  outOfBandCount: number;
  /** How many points were examined. */
  windowSize: number;
}

/**
 * Status of a trend series over its **whole plotted window**, not just its
 * newest point.
 *
 * The panel used to badge a chart from `latest_value` alone. Drone 106 spent a
 * month plotting 4.3 million % while the badge read NORMAL, because the most
 * recent flight happened to land back inside 80–100 % (#1269). A chart that
 * shows a range violation must say so, whichever flight produced it.
 *
 * A point whose value is missing or non-finite counts as out of band: the
 * range cannot be confirmed, and silently passing it is what caused this bug.
 */
export function windowHealth(
  points: Pick<TrendChartPoint, 'value'>[] | undefined,
  range: AnalysisRange | undefined
): WindowHealth {
  if (!range || !points || points.length === 0) {
    return { status: 'unknown', outOfBandCount: 0, windowSize: points?.length ?? 0 };
  }

  const outOfBandCount = points.filter(
    (p) =>
      typeof p.value !== 'number' ||
      !Number.isFinite(p.value) ||
      p.value < range.min ||
      p.value > range.max
  ).length;

  return {
    status: outOfBandCount > 0 ? 'warning' : 'normal',
    outOfBandCount,
    windowSize: points.length,
  };
}
